import { Actions, createEffect, ofType } from '@ngrx/effects';
import { OrderingCartsService } from '@services/api/internal/ordering-carts.service';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { AlertService } from '@services/utils/alert.service';
import { AlertType } from '@shared/enums/notifications/notifications.enum';
import { Injectable } from '@angular/core';
import {
  getCart,
  getCartFailure,
  getCartShortInfo,
  getCartShortInfoFailure,
  getCartShortInfoSuccess,
  getCartSuccess,
  getMarkupSettings,
  getMarkupSettingsFailure,
  getMarkupSettingsSuccess,
} from './cart.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { ContextFacade, ContextState } from '../../core';
import { ContractInfo } from '@shared/interfaces/ordering-carts/ordering-carts.interface';
import { MarkupsSettingsService } from '@services/api/internal/markups-settings.service';

@Injectable()
export class CartEffects {
  getCartShortInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCartShortInfo),
      withLatestFrom(this.contextFacade.context),
      switchMap(([_, { contract }]: [unknown, ContextState]) => {
        const payload: ContractInfo = {
          contractId: contract?.id,
        };
        // catchError stays inside switchMap: a failed request must not complete the effect stream.
        return this.orderingCartService.getCurrentShortInfo(payload).pipe(
          map(info => getCartShortInfoSuccess({ info })),
          catchError((error: HttpErrorResponse) => {
            this.alertService.show({ severity: AlertType.Error, detail: error.error?.messages?.[0] });
            return of(getCartShortInfoFailure());
          }),
        );
      }),
    ),
  );

  getCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCart),
      switchMap(action =>
        this.orderingCartService.getCurrentCart(action.cart).pipe(
          map(cart => getCartSuccess({ cart })),
          catchError((error: HttpErrorResponse) => {
            this.alertService.show({ severity: AlertType.Error, summary: error.error?.messages?.[0] });
            return of(getCartFailure());
          }),
        ),
      ),
    ),
  );

  getMarkupSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getMarkupSettings),
      switchMap(() =>
        this.markupSettingsService.getMarkupsSettings().pipe(
          map(markupSettings => getMarkupSettingsSuccess({ markupSettings })),
          catchError(() => {
            this.alertService.show({
              severity: AlertType.Error,
              detail: 'MARKUPS_SETTINGS.ERROR_FETCH',
            });
            return of(getMarkupSettingsFailure());
          }),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private orderingCartService: OrderingCartsService,
    private alertService: AlertService,
    private contextFacade: ContextFacade,
    private markupSettingsService: MarkupsSettingsService,
  ) {}
}
